import type { FC } from "react"
import React, { useState } from "react"
import type { StackProps } from "@chakra-ui/react"
import { Stack, Skeleton, Icon, Text } from "@chakra-ui/react"

import CardContainer from "common/components/CardContainer"
import ChevronRight from "ui/components/icons/ChevronRight"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import GenericModal from "ui/components/GenericModal"
import SimulationResultBadge from "proposal/components/simulation/SimulationResultBadge"
import { useProposalSimulation } from "tallyProposal/hooks/useProposalSimulation"
import ProposalImpactDetails from "proposal/components/simulation/ProposalImpactDetails"
import type { ExecutableCall } from "query/graphql"

export type ProposalExecutableCall = Pick<
  ExecutableCall,
  "value" | "target" | "calldata" | "signature" | "type"
>

type Props = {
  proposalId: string
  executableCalls: ProposalExecutableCall[]
  chainId: string
}

const ProposalImpactOverview: FC<Props & StackProps> = ({
  proposalId,
  chainId,
  executableCalls,
  ...stackProps
}) => {
  const { isFeatureFlagOn } = useGovernanceContext()
  const hideSimulation = isFeatureFlagOn(FeatureFlag.HideSimulation)

  const {
    overallResult,
    simulationsResult,
    threatAnalysisResult,
    simulations,
    threatAnalysis,
    isSecurityCheckSupported,
    isLoading,
    isError,
    createdAt,
  } = useProposalSimulation({
    proposalId,
    chainId,
  })

  const [isModalOpen, setModalOpen] = useState(false)

  const toggleModal = () => setModalOpen(!isModalOpen)

  if (!isSecurityCheckSupported || hideSimulation) {
    return null
  }

  if (isLoading) {
    return (
      <CardContainer title="Impact Overview">
        <Stack as="section" {...stackProps}>
          <Skeleton h="2.784375em" w="full" />
          <Skeleton h="2.784375em" w="full" />
          <Skeleton h="2.784375em" w="full" />
        </Stack>
      </CardContainer>
    )
  }

  if (executableCalls.length <= 0) {
    return (
      <CardContainer title="Impact Overview">
        <Stack as="section" pt={{ base: 4, sm: 0 }} spacing={4} {...stackProps}>
          <Text>
            This proposal has no associated actions and will have no impact on
            the treasury or any associated smart contracts
          </Text>
        </Stack>
      </CardContainer>
    )
  }

  if (!isLoading && !isError && overallResult) {
    return (
      <>
        <CardContainer title="Impact Overview">
          <Stack
            align="center"
            cursor="pointer"
            direction="row"
            justify="space-between"
            pt={{ base: "4", md: "0" }}
            onClick={toggleModal}
          >
            <SimulationResultBadge
              label={{
                success: "All simulations passed",
                failed: "Review advised",
                warning: "Some simulations are not available",
              }}
              result={overallResult}
            />
            <Icon as={ChevronRight} fill="gray.400" h={3} w={3} />
          </Stack>
        </CardContainer>

        <GenericModal
          bgColor="gray.50"
          isOpen={isModalOpen}
          maxWidth="600px"
          title="Simulation report"
          onClose={toggleModal}
        >
          <ProposalImpactDetails
            createdAt={createdAt}
            executableCalls={executableCalls}
            simulations={simulations}
            simulationsResult={simulationsResult}
            threatAnalysis={threatAnalysis}
            threatAnalysisResult={threatAnalysisResult}
          />
        </GenericModal>
      </>
    )
  }

  return (
    <CardContainer title="Impact Overview">
      <Stack as="section" pt={{ base: 4, sm: 0 }} spacing={4} {...stackProps}>
        <Text>Impact overview is not available for this proposal</Text>
      </Stack>
    </CardContainer>
  )
}

export default ProposalImpactOverview
